import {
  Dispatch,
  FormEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import {
  ThemeRouteFormMode,
  ThemeRouteFormValues,
  ThemeRouteLanguageCode,
  ThemeRouteCustomWaypoint,
  ThemeRoutePoiRef,
  ThemeRouteThemeKey,
  ThemeRouteTranslations,
  ThemeRoute,
  ThemeRouteType,
} from '@/types/theme-routes.types';
import {
  useGetThemeRouteByIdQuery,
  useCreateThemeRouteMutation,
  useUpdateThemeRouteMutation,
  useGetThemeRouteCommentsQuery,
} from '@/services/ThemeRoutesService';
import { formatFinnishCoordinate, formatFinnishNumber } from '@/utils';
import {
  useGetHarborsQuery,
  useTranslateHarborContentMutation,
  useGetHarborByIdQuery,
} from '@/services/HarborManagementService';
import { useGetPOIsQuery } from '@/services/POIManagementService';
import { HarborResponse } from '@/types/harbor-management.types';
import { POI } from '@/types/poi-management.types';
import { SeaRouteService } from '@/services/SeaRouteService';
import ThemeRoutePreviewModal from './ThemeRoutePreviewModal';
import { usePermissions } from '@/hooks/usePermissions';
import MediaUpload from '../habor-management/MediaUpload';
import { useMapboxAccessToken } from '@/hooks/useMapboxAccessToken';
import { formatDisplayDateTime } from '@/utils/Date';

interface ThemeRouteFormPageProps {
  mode: ThemeRouteFormMode;
  embedded?: boolean;
  embeddedRouteId?: string;
  backTargetOverride?: string;
  onBack?: () => void;
  fixedHarborId?: string;
  routeType?: ThemeRouteType;
  routingMode?: 'sea' | 'straight';
  permissionScope?: 'theme_routes' | 'tourism';
  enablePoiHarborConnection?: boolean;
  hideComments?: boolean;
}

const THEME_ROUTE_THEME_OPTIONS: {
  value: ThemeRouteThemeKey;
  label: string;
}[] = [
  { value: 'coastal_nature', label: 'Coastal nature' },
  { value: 'family_friendly', label: 'Family friendly' },
  { value: 'cultural_heritage', label: 'Cultural heritage' },
  { value: 'island_hopping', label: 'Island hopping' },
  { value: 'harbor_to_harbor', label: 'Harbor to harbor' },
];

const buildInitialTranslations = (defaultLanguage: ThemeRouteLanguageCode) => {
  const base: Record<
    ThemeRouteLanguageCode,
    { name?: string; description?: string }
  > = {
    en: {},
    de: {},
    fi: {},
    sv: {},
  };
  return {
    ...base,
    [defaultLanguage]: { ...base[defaultLanguage] },
  };
};

const ThemeRouteFormPage = ({
  mode,
  embedded = false,
  embeddedRouteId,
  backTargetOverride,
  onBack,
  fixedHarborId,
  routeType = 'themed',
  routingMode = 'sea',
  permissionScope = 'theme_routes',
  enablePoiHarborConnection = false,
  hideComments = false,
}: ThemeRouteFormPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const resolvedId = embeddedRouteId || id;
  const intl = useIntl();
  const { currentLanguage, changeLanguage } = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const backTarget =
    backTargetOverride ||
    ((location.state as { from?: string } | null)?.from === 'approvals'
      ? '/theme-routes/approvals'
      : '/theme-routes');

  const isViewMode = mode === 'view';

  const isEditOrView = mode === 'edit' || mode === 'view';

  const canCreate =
    permissionScope === 'tourism'
      ? hasPermission('tourism_create')
      : hasPermission('theme_route_management_create');
  const canUpdate =
    permissionScope === 'tourism'
      ? hasPermission('tourism_update')
      : hasPermission('theme_route_management_update');
  const canSubmit =
    mode === 'create' ? canCreate : mode === 'edit' ? canUpdate : false;

  const [formValues, setFormValues] = useState<ThemeRouteFormValues>(() => {
    const defaultLang = (currentLanguage.code ||
      'en') as ThemeRouteLanguageCode;
    return {
      id: undefined,
      type: routeType,
      defaultLanguage: defaultLang,
      name: '',
      description: '',
      translations: buildInitialTranslations(defaultLang),
      themeKey: 'coastal_nature',
      status: 'draft',
      coverImage: null,
      coverImage_captions: [],
      poiSequence: [],
      customWaypoints: [],
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mapRef] = useState<{ map: mapboxgl.Map | null }>({ map: null });
  const [seaRouteCoords, setSeaRouteCoords] = useState<[number, number][]>([]);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [seaRouteDistanceNm, setSeaRouteDistanceNm] = useState<number | null>(
    null
  );
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isAddingCustomWaypoint, setIsAddingCustomWaypoint] = useState(false);
  const [expandedCustomWaypointIndex, setExpandedCustomWaypointIndex] =
    useState<number | null>(null);
  const [webglError, setWebglError] = useState(false);

  const { isMapboxReady } = useMapboxAccessToken();
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const { data: existingRoute, isLoading: isRouteLoading } =
    useGetThemeRouteByIdQuery(resolvedId as string, {
      skip: !resolvedId || mode === 'create',
    });

  const { data: commentsResponse, isLoading: isCommentsLoading } =
    useGetThemeRouteCommentsQuery(resolvedId as string, {
      skip: !resolvedId || !isViewMode || hideComments,
    });

  const [createThemeRoute] = useCreateThemeRouteMutation();
  const [updateThemeRoute] = useUpdateThemeRouteMutation();
  const [translateThemeRoute] = useTranslateHarborContentMutation();

  useEffect(() => {
    if (!isEditOrView || !resolvedId) return;
    if (isRouteLoading) return;

    if (!existingRoute) {
      enqueueSnackbar('Theme route not found', { variant: 'error' });
      if (!embedded) {
        navigate('/theme-routes');
      }
      return;
    }

    const baseTranslation =
      existingRoute.translations[existingRoute.defaultLanguage] || {};

    const coverImageCaptions = existingRoute.image_captions
      ? [
          {
            image_index: existingRoute.image_captions.image_index || 0,
            captions: existingRoute.image_captions.captions || {},
          },
        ]
      : [];

    setFormValues({
      id: existingRoute.id,
      type: existingRoute.type || routeType,
      defaultLanguage: existingRoute.defaultLanguage,
      name: existingRoute.name || baseTranslation.name || '',
      description:
        typeof existingRoute.description === 'string'
          ? existingRoute.description
          : baseTranslation.description || '',
      translations: existingRoute.translations,
      themeKey: existingRoute.themeKey,
      status: existingRoute.status,
      coverImage:
        Array.isArray(existingRoute.media) &&
        typeof existingRoute.media[0] === 'string'
          ? existingRoute.media[0]
          : null,
      coverImage_captions: coverImageCaptions,
      poiSequence: existingRoute.poiSequence,
      customWaypoints: existingRoute.customWaypoints || [],
    });
  }, [
    existingRoute,
    resolvedId,
    isEditOrView,
    isRouteLoading,
    navigate,
    enqueueSnackbar,
    embedded,
    routeType,
  ]);

  const handleAddCustomWaypointAt = useCallback((lng: number, lat: number) => {
    setFormValues((prev) => {
      const nextWaypoints = Array.isArray(prev.customWaypoints)
        ? [...prev.customWaypoints]
        : [];

      nextWaypoints.push({
        lat,
        lon: lng,
        label: '',
        description: '',
      });

      return {
        ...prev,
        customWaypoints: nextWaypoints,
      };
    });
    setErrors((prev) => ({ ...prev, customWaypoints: '' }));
  }, []);

  const handleUpdateCustomWaypoint = (
    index: number,
    patch: Partial<ThemeRouteCustomWaypoint>
  ) => {
    setFormValues((prev) => {
      const next = Array.isArray(prev.customWaypoints)
        ? [...prev.customWaypoints]
        : [];
      const current = next[index];
      if (!current) return prev;

      if (Object.prototype.hasOwnProperty.call(patch, 'description')) {
        const desc = patch.description;
        const langCode = (currentLanguage.code ||
          prev.defaultLanguage) as ThemeRouteLanguageCode;

        if (langCode === prev.defaultLanguage) {
          next[index] = {
            ...current,
            ...patch,
            description: typeof desc === 'string' ? desc : '',
          };
        } else {
          next[index] = {
            ...current,
            ...patch,
            description: current.description,
            translations: {
              ...(current.translations || {}),
              [langCode]: {
                ...(current.translations?.[langCode] || {}),
                description: typeof desc === 'string' ? desc : '',
              },
            },
          };
        }
      } else {
        next[index] = {
          ...current,
          ...patch,
        };
      }
      return {
        ...prev,
        customWaypoints: next,
      };
    });
    setErrors((prev) => ({ ...prev, customWaypoints: '' }));
  };

  const handleRemoveCustomWaypoint = (index: number) => {
    setFormValues((prev) => {
      const nextWaypoints = Array.isArray(prev.customWaypoints)
        ? prev.customWaypoints.filter((_, i) => i !== index)
        : [];

      return {
        ...prev,
        customWaypoints: nextWaypoints,
      };
    });
  };

  const [selectedHarborId, setSelectedHarborId] = useState<string>(
    fixedHarborId || ''
  );

  const { data: fixedHarborDetails } = useGetHarborByIdQuery(
    fixedHarborId || '',
    {
      skip: !fixedHarborId,
    }
  );

  const fixedHarborCoords = useMemo(() => {
    if (!fixedHarborId || !fixedHarborDetails) return null;

    const raw = fixedHarborDetails as unknown;
    const asAny = (
      raw &&
      typeof raw === 'object' &&
      'data' in (raw as Record<string, unknown>)
        ? ((raw as Record<string, unknown>).data as unknown)
        : raw
    ) as {
      id?: string;
      name?: string;
      latitude?: number | string | null;
      longitude?: number | string | null;
    };

    const lat = toNumber(asAny.latitude);
    const lng = toNumber(asAny.longitude);
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;

    return {
      id: fixedHarborId,
      name: typeof asAny.name === 'string' ? asAny.name : undefined,
      lat,
      lng,
    };
  }, [fixedHarborDetails, fixedHarborId]);
  useEffect(() => {
    if (fixedHarborId && fixedHarborId !== selectedHarborId) {
      setSelectedHarborId(fixedHarborId);
    }
  }, [fixedHarborId, selectedHarborId]);

  const [harborSearchTerm, setHarborSearchTerm] = useState('');
  const [debouncedHarborSearch, setDebouncedHarborSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHarborSearch(harborSearchTerm.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [harborSearchTerm]);

  const { data: harborsData } = useGetHarborsQuery(
    {
      page: 1,
      limit: 100,
      search: debouncedHarborSearch || undefined,
    },
    { skip: !!fixedHarborId }
  );

  const { data: poisResponse, isLoading: isPoisLoading } = useGetPOIsQuery(
    {
      page: 1,
      limit: 100,
      harborId: selectedHarborId || '',
    },
    { skip: !selectedHarborId }
  );

  const harborOptions = useMemo(() => {
    const apiHarbors = (harborsData?.data as HarborResponse[]) || [];

    return apiHarbors.map((harbor) => ({
      value: harbor.id,
      label: harbor.name,
    }));
  }, [harborsData]);

  useEffect(() => {
    const apiHarbors = (harborsData?.data as HarborResponse[]) || [];

    if (!debouncedHarborSearch) {
      return;
    }

    if (!apiHarbors.length) {
      return;
    }

    const existsInResults = apiHarbors.some(
      (harbor) => harbor.id === selectedHarborId
    );

    if (!selectedHarborId || !existsInResults) {
      setSelectedHarborId(apiHarbors[0].id);
    }
  }, [harborsData, debouncedHarborSearch, selectedHarborId, fixedHarborId]);

  const poiOptions = useMemo(() => {
    const list = (poisResponse?.data as POI[]) || [];
    return list;
  }, [poisResponse]);

  const activeLanguage = (currentLanguage.code ||
    formValues.defaultLanguage) as ThemeRouteLanguageCode;

  const activeTranslation = formValues.translations[activeLanguage];

  const getWaypointDescriptionForLanguage = useCallback(
    (waypoint: ThemeRouteCustomWaypoint, lang: ThemeRouteLanguageCode) => {
      const translated = waypoint.translations?.[lang]?.description;
      if (typeof translated === 'string') return translated;

      if (lang === formValues.defaultLanguage) {
        return typeof waypoint.description === 'string'
          ? waypoint.description
          : '';
      }

      return '';
    },
    [formValues.defaultLanguage]
  );

  interface ThemeRouteCommentItem {
    id: string;
    theme_route_id: string;
    user_id: string;
    user?: { name?: string; email?: string };
    comment: string;
    parent_comment_id?: string | null;
    is_deleted?: boolean;
    created_at?: string;
    updated_at?: string;
  }

  const comments: ThemeRouteCommentItem[] = useMemo(() => {
    if (!commentsResponse || !Array.isArray(commentsResponse.data)) {
      return [];
    }
    return commentsResponse.data as ThemeRouteCommentItem[];
  }, [commentsResponse]);

  useEffect(() => {
    const baseStops: { lng: number; lat: number }[] = [];

    // In Tourism Highlights we want POI coordinates for routing (harbor is the start).
    // In Theme Routes Management we want Harbor-to-Harbor routing.
    const pickStopCoords = (poi: ThemeRoutePoiRef) => {
      if (enablePoiHarborConnection) {
        if (
          typeof poi.longitude === 'number' &&
          typeof poi.latitude === 'number'
        ) {
          return { lng: poi.longitude, lat: poi.latitude };
        }
        return null;
      }

      if (
        typeof poi.harborLongitude === 'number' &&
        typeof poi.harborLatitude === 'number'
      ) {
        return { lng: poi.harborLongitude, lat: poi.harborLatitude };
      }

      return null;
    };

    // Tourism Highlights: Always start from fixed harbor (if available)
    if (enablePoiHarborConnection && fixedHarborCoords) {
      baseStops.push({
        lng: fixedHarborCoords.lng,
        lat: fixedHarborCoords.lat,
      });
    }

    // Theme Routes Management: use unique harbors (in order) to form Harbor-to-Harbor route.
    if (!enablePoiHarborConnection) {
      const seen = new Set<string>();
      formValues.poiSequence.forEach((poi) => {
        if (
          typeof poi.harborLongitude !== 'number' ||
          typeof poi.harborLatitude !== 'number'
        ) {
          return;
        }

        const key =
          poi.harborId ||
          poi.harborName ||
          `${poi.harborLongitude}-${poi.harborLatitude}`;
        if (seen.has(key)) return;
        seen.add(key);

        baseStops.push({ lng: poi.harborLongitude, lat: poi.harborLatitude });
      });

      if (baseStops.length < 2) {
        setSeaRouteCoords([]);
        setSeaRouteDistanceNm(null);
        setIsCalculatingRoute(false);
        return;
      }
    }

    if (enablePoiHarborConnection) {
      formValues.poiSequence.forEach((poi) => {
        const coords = pickStopCoords(poi);
        if (!coords) return;

        const last = baseStops[baseStops.length - 1];
        if (!last || last.lng !== coords.lng || last.lat !== coords.lat) {
          baseStops.push(coords);
        }
      });
    }

    const waypointStops = (
      Array.isArray(formValues.customWaypoints)
        ? formValues.customWaypoints
        : []
    )
      .filter(
        (waypoint) =>
          typeof waypoint.lon === 'number' && typeof waypoint.lat === 'number'
      )
      .map((waypoint) => ({
        lng: waypoint.lon,
        lat: waypoint.lat,
      }));

    // Distribute custom waypoints across route legs (between each consecutive pair)
    // so multi-stop routes can be shaped on every segment (e.g. POI1 -> POI2).
    const buildStopsWithWaypoints = (
      mainStops: { lng: number; lat: number }[],
      waypoints: { lng: number; lat: number }[]
    ) => {
      if (mainStops.length < 2 || waypoints.length === 0) return mainStops;

      const legCount = mainStops.length - 1;
      const waypointsByLeg: Array<
        Array<{ lng: number; lat: number; t: number }>
      > = Array.from({ length: legCount }, () => []);

      const toXY = (p: { lng: number; lat: number }, refLat: number) => {
        const rad = Math.PI / 180;
        const x = p.lng * rad * Math.cos(refLat * rad);
        const y = p.lat * rad;
        return { x, y };
      };

      const closestLegIndex = (w: { lng: number; lat: number }) => {
        let bestIdx = 0;
        let bestDist = Number.POSITIVE_INFINITY;
        let bestT = 0;

        for (let i = 0; i < legCount; i += 1) {
          const a = mainStops[i];
          const b = mainStops[i + 1];
          const refLat = (a.lat + b.lat + w.lat) / 3;
          const A = toXY(a, refLat);
          const B = toXY(b, refLat);
          const W = toXY(w, refLat);

          const abx = B.x - A.x;
          const aby = B.y - A.y;
          const awx = W.x - A.x;
          const awy = W.y - A.y;
          const abLen2 = abx * abx + aby * aby;
          const tRaw = abLen2 > 0 ? (awx * abx + awy * aby) / abLen2 : 0;
          const t = Math.max(0, Math.min(1, tRaw));
          const px = A.x + t * abx;
          const py = A.y + t * aby;
          const dx = W.x - px;
          const dy = W.y - py;
          const d2 = dx * dx + dy * dy;

          if (d2 < bestDist) {
            bestDist = d2;
            bestIdx = i;
            bestT = t;
          }
        }

        return { index: bestIdx, t: bestT };
      };

      waypoints.forEach((w) => {
        const { index, t } = closestLegIndex(w);
        waypointsByLeg[index].push({ ...w, t });
      });

      waypointsByLeg.forEach((list) => {
        list.sort((a, b) => a.t - b.t);
      });

      const expanded: { lng: number; lat: number }[] = [];
      for (let i = 0; i < legCount; i += 1) {
        const start = mainStops[i];
        const end = mainStops[i + 1];
        if (expanded.length === 0) {
          expanded.push(start);
        }
        waypointsByLeg[i].forEach((w) => {
          const last = expanded[expanded.length - 1];
          if (!last || last.lng !== w.lng || last.lat !== w.lat) {
            expanded.push({ lng: w.lng, lat: w.lat });
          }
        });
        const last = expanded[expanded.length - 1];
        if (!last || last.lng !== end.lng || last.lat !== end.lat) {
          expanded.push(end);
        }
      }

      return expanded;
    };

    const stops: { lng: number; lat: number }[] = buildStopsWithWaypoints(
      baseStops,
      waypointStops
    );

    if (stops.length < 2) {
      setSeaRouteCoords([]);
      setSeaRouteDistanceNm(null);
      setIsCalculatingRoute(false);
      return;
    }

    if (routingMode === 'straight') {
      const coords = stops.map(
        (stop) => [stop.lng, stop.lat] as [number, number]
      );

      const toRad = (deg: number) => (deg * Math.PI) / 180;
      const nmPerKm = 0.5399568;
      const earthRadiusKm = 6371;
      const haversineNm = (
        a: { lng: number; lat: number },
        b: { lng: number; lat: number }
      ) => {
        const dLat = toRad(b.lat - a.lat);
        const dLon = toRad(b.lng - a.lng);
        const lat1 = toRad(a.lat);
        const lat2 = toRad(b.lat);
        const h =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(lat1) *
            Math.cos(lat2) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
        return earthRadiusKm * c * nmPerKm;
      };

      const distance = stops.reduce((sum, stop, idx) => {
        if (idx === 0) return 0;
        return sum + haversineNm(stops[idx - 1], stop);
      }, 0);

      setSeaRouteCoords(coords);
      setSeaRouteDistanceNm(distance);
      setIsCalculatingRoute(false);
      return;
    }

    let cancelled = false;

    const fetchRoute = async () => {
      try {
        setIsCalculatingRoute(true);
        const legs: Array<{
          origin: [number, number];
          destination: [number, number];
        }> = [];

        for (let i = 0; i < stops.length - 1; i += 1) {
          const start = stops[i];
          const end = stops[i + 1];
          legs.push({
            origin: [start.lng, start.lat],
            destination: [end.lng, end.lat],
          });
        }

        const results = await Promise.all(
          legs.map((leg) =>
            SeaRouteService.calculateRoute({
              origin: leg.origin,
              destination: leg.destination,
              grid: 'G1',
              routing_engine: 'python',
              min_height: 2,
            })
          )
        );

        if (cancelled) return;

        const combinedCoords: [number, number][] = [];
        results.forEach((result, index) => {
          const coords = result.route.geometry.coordinates || [];
          if (!coords.length) return;
          if (index === 0) {
            combinedCoords.push(...coords);
          } else {
            combinedCoords.push(...coords.slice(1));
          }
        });

        const totalDistance = results.reduce(
          (sum, result) => sum + (result.distance || 0),
          0
        );

        setSeaRouteCoords(combinedCoords);
        setSeaRouteDistanceNm(totalDistance);
      } catch {
        // Silent failure for now; clear any existing sea route
        setSeaRouteCoords([]);
        setSeaRouteDistanceNm(null);
      } finally {
        if (!cancelled) {
          setIsCalculatingRoute(false);
        }
      }
    };

    fetchRoute();

    return () => {
      cancelled = true;
    };
  }, [
    formValues.poiSequence,
    formValues.customWaypoints,
    routingMode,
    fixedHarborCoords,
    enablePoiHarborConnection,
  ]);

  useEffect(() => {
    const mapInstance = mapRef.map;
    if (!mapInstance) return;

    const handleMapClick = (event: mapboxgl.MapMouseEvent) => {
      if (isViewMode || isSubmitting) return;
      if (!isAddingCustomWaypoint) return;

      const { lng, lat } = event.lngLat;
      handleAddCustomWaypointAt(lng, lat);
    };

    if (typeof (mapInstance as unknown as { on?: unknown }).on === 'function') {
      (
        mapInstance as unknown as { on: (type: string, cb: unknown) => void }
      ).on('click', handleMapClick);
    }

    return () => {
      if (
        typeof (mapInstance as unknown as { off?: unknown }).off === 'function'
      ) {
        (
          mapInstance as unknown as {
            off: (type: string, cb: unknown) => void;
          }
        ).off('click', handleMapClick);
      }
    };
  }, [
    handleAddCustomWaypointAt,
    isAddingCustomWaypoint,
    isSubmitting,
    isViewMode,
    mapRef,
  ]);

  const handleNameChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      name: value,
    }));
    setErrors((prev) => ({ ...prev, name: '' }));
  };

  const handleDescriptionChange = (value: string) => {
    const langCode = (currentLanguage.code ||
      formValues.defaultLanguage) as ThemeRouteLanguageCode;
    setFormValues((prev) => {
      if (langCode === prev.defaultLanguage) {
        return {
          ...prev,
          description: value,
          translations: {
            ...prev.translations,
            [langCode]: {
              ...prev.translations[langCode],
              description: value,
            },
          },
        };
      }
      return {
        ...prev,
        translations: {
          ...prev.translations,
          [langCode]: {
            ...prev.translations[langCode],
            description: value,
          },
        },
      };
    });
  };

  const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as ThemeRouteThemeKey;
    setFormValues((prev) => ({ ...prev, themeKey: value }));
  };

  const handleOpenPreview = () => {
    if (!formValues.description || !formValues.description.trim()) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'THEME_ROUTES.VALIDATION.DESCRIPTION_REQUIRED_FOR_PREVIEW',
          defaultMessage: 'Please enter description to translate',
        }),
        {
          variant: 'warning',
        }
      );
      return;
    }
    setIsPreviewModalOpen(true);
  };

  const handleTranslationsGenerated = (updated: ThemeRouteTranslations) => {
    setFormValues((prev) => {
      const next: ThemeRouteFormValues = {
        ...prev,
        translations: updated,
      };
      const baseDesc = updated[prev.defaultLanguage]?.description;
      if (typeof baseDesc === 'string') {
        next.description = baseDesc;
      }
      return next;
    });
  };

  function toNumber(value: number | string | null | undefined) {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  }

  const handleAddPoi = (poi: POI) => {
    const harborFromPoi = poi.harbors?.[0];

    const harborList = (harborsData?.data as HarborResponse[]) || [];
    const harborFromListByRelation = harborFromPoi?.id
      ? harborList.find((h) => h.id === harborFromPoi.id)
      : undefined;

    const harborFromListById =
      !harborFromListByRelation && poi.harbor_id
        ? harborList.find((h) => h.id === poi.harbor_id)
        : undefined;

    const harborFromListBySelected =
      !harborFromListByRelation && !harborFromListById && selectedHarborId
        ? harborList.find((h) => h.id === selectedHarborId)
        : undefined;

    const resolvedHarbor =
      harborFromListByRelation ||
      harborFromListById ||
      harborFromListBySelected;

    const harborId =
      harborFromPoi?.id ??
      resolvedHarbor?.id ??
      poi.harbor_id ??
      selectedHarborId ??
      null;

    const harborName = harborFromPoi?.name ?? resolvedHarbor?.name;

    const harborLatitude = toNumber(
      harborFromPoi?.latitude ?? resolvedHarbor?.latitude
    );

    const harborLongitude = toNumber(
      harborFromPoi?.longitude ?? resolvedHarbor?.longitude
    );

    const primaryImage =
      Array.isArray(poi.images) && poi.images.length > 0
        ? poi.images[0]
        : undefined;

    const poiRef: ThemeRoutePoiRef = {
      poiId: poi.id as string,
      harborId,
      poiName: poi.name,
      harborName,
      latitude: toNumber(poi.latitude as unknown as string | number | null),
      longitude: toNumber(poi.longitude as unknown as string | number | null),
      description: poi.description,
      imageUrl: primaryImage,
      harborLatitude,
      harborLongitude,
    };

    setFormValues((prev) => ({
      ...prev,
      poiSequence: [...prev.poiSequence, poiRef],
    }));
    setErrors((prev) => ({ ...prev, poiSequence: '' }));
  };

  const handleAddHarborStop = () => {
    if (!selectedHarborId) return;

    const harborList = (harborsData?.data as HarborResponse[]) || [];
    const harbor = harborList.find((h) => h.id === selectedHarborId);
    if (!harbor) return;

    const harborLatitude = toNumber(harbor.latitude);
    const harborLongitude = toNumber(harbor.longitude);

    const harborRef: ThemeRoutePoiRef = {
      poiId: null,
      harborId: harbor.id,
      poiName: harbor.name,
      harborName: harbor.name,
      harborLatitude,
      harborLongitude,
    };

    setFormValues((prev) => ({
      ...prev,
      poiSequence: [...prev.poiSequence, harborRef],
    }));
    setErrors((prev) => ({ ...prev, poiSequence: '' }));
  };

  const handleRemovePoi = (index: number) => {
    setFormValues((prev) => ({
      ...prev,
      poiSequence: prev.poiSequence.filter((_, i) => i !== index),
    }));
  };

  const movePoi = (fromIndex: number, toIndex: number) => {
    setFormValues((prev) => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= prev.poiSequence.length ||
        toIndex >= prev.poiSequence.length
      ) {
        return prev;
      }
      const next = [...prev.poiSequence];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { ...prev, poiSequence: next };
    });
  };

  const handlePoiKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      movePoi(index, index - 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      movePoi(index, index + 1);
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      handleRemovePoi(index);
    }
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};
    if (!formValues.name || formValues.name.trim() === '') {
      nextErrors.name = intl.formatMessage({
        id: 'THEME_ROUTES.VALIDATION.NAME_REQUIRED',
        defaultMessage: 'Name is required in the default language',
      });
    }

    const customWaypoints = Array.isArray(formValues.customWaypoints)
      ? formValues.customWaypoints
      : [];

    if (
      customWaypoints.some(
        (waypoint) =>
          typeof waypoint.lat !== 'number' ||
          typeof waypoint.lon !== 'number' ||
          !waypoint.label ||
          waypoint.label.trim() === ''
      )
    ) {
      nextErrors.customWaypoints = intl.formatMessage({
        id: 'THEME_ROUTES.VALIDATION.CUSTOM_WAYPOINTS_REQUIRED',
        defaultMessage:
          'Custom waypoints must have latitude, longitude, and a label',
      });
    }

    if (
      formValues.status === 'published' &&
      formValues.poiSequence.length < 2
    ) {
      nextErrors.poiSequence = intl.formatMessage({
        id: 'THEME_ROUTES.VALIDATION.MIN_POIS_PUBLISHED',
        defaultMessage:
          'Published routes must include at least two POIs in the sequence',
      });
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (isViewMode) {
      if (embedded && onBack) {
        onBack();
      } else {
        navigate(backTarget);
      }
      return;
    }

    if (!canSubmit) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'THEME_ROUTES.ERROR.PERMISSION_DENIED',
          defaultMessage: "You don't have permission to perform this action",
        }),
        { variant: 'error' }
      );
      return;
    }

    if (!validateForm()) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'THEME_ROUTES.ERROR.FORM',
          defaultMessage: 'Please fix the form errors',
        }),
        { variant: 'error' }
      );
      return;
    }

    try {
      setIsSubmitting(true);
      let translations = formValues.translations;

      const defaultLang = formValues.defaultLanguage;
      const baseDesc =
        (formValues.description && formValues.description.trim()) ||
        translations[defaultLang]?.description ||
        '';

      const hasNonDefaultDescription = Object.entries(translations).some(
        ([code, value]) => {
          const langCode = code as ThemeRouteLanguageCode;
          if (langCode === defaultLang) return false;
          const desc = value?.description;
          return typeof desc === 'string' && desc.trim() !== '';
        }
      );

      if (baseDesc && !hasNonDefaultDescription) {
        try {
          const result = await translateThemeRoute({
            name: formValues.name,
            description: baseDesc,
            source_language: defaultLang,
          }).unwrap();

          if (result && result.translations) {
            translations = {
              ...translations,
              ...result.translations,
            };
          }
        } catch {
          enqueueSnackbar(
            intl.formatMessage({
              id: 'THEME_ROUTES.PREVIEW.TRANSLATION_ERROR',
              defaultMessage: 'Failed to generate translations',
            }),
            { variant: 'error' }
          );
        }
      }

      const formData = new FormData();

      formData.append('defaultLanguage', formValues.defaultLanguage);
      formData.append('name', formValues.name.trim());

      if (formValues.description && formValues.description.trim()) {
        formData.append('description', formValues.description.trim());
      }

      const resolvedType = (formValues.type || routeType) as ThemeRouteType;
      formData.append('type', resolvedType === 'harbor' ? '1' : '2');

      formData.append('theme', formValues.themeKey);

      let numericStatus =
        mode === 'create' && (formValues.type || routeType) === 'harbor'
          ? 1
          : 0;

      if (mode !== 'create') {
        switch (formValues.status) {
          case 'draft':
            numericStatus = 0;
            break;
          case 'published':
            numericStatus = 1;
            break;
          case 'pending_approval':
            numericStatus = 2;
            break;
          case 'approved':
            numericStatus = 3;
            break;
          case 'rejected':
            numericStatus = 4;
            break;
          default:
            numericStatus = 0;
            break;
        }
      }

      formData.append('status', String(numericStatus));

      formData.append('translations', JSON.stringify(translations));

      const poisDto = formValues.poiSequence.map((poi, index) => {
        const dto: {
          poi_id: string;
          order: number;
          harbor_id?: string | null;
        } = {
          poi_id: poi.poiId || '',
          order: index + 1,
        };

        if (poi.harborId) {
          dto.harbor_id = poi.harborId;
        }

        return dto;
      });

      formData.append('pois', JSON.stringify(poisDto));

      const customWaypoints = Array.isArray(formValues.customWaypoints)
        ? formValues.customWaypoints
        : [];
      if (customWaypoints.length > 0) {
        const customWaypointsPayload =
          mode === 'create'
            ? // eslint-disable-next-line @typescript-eslint/no-unused-vars
              customWaypoints.map(({ id: _id, ...rest }) => rest)
            : customWaypoints;
        formData.append(
          'custom_waypoints',
          JSON.stringify(customWaypointsPayload)
        );
      }

      if (mode === 'edit') {
        const original = Array.isArray(existingRoute?.customWaypoints)
          ? existingRoute.customWaypoints
          : [];

        const makeKey = (w: { lat: number; lon: number; label: string }) =>
          `${w.lat}|${w.lon}|${w.label}`;

        const currentKeys = new Set(customWaypoints.map(makeKey));

        const removedWaypoints = original
          .filter((w) => !currentKeys.has(makeKey(w)))
          .map(({ lat, lon, label, description }) => ({
            lat,
            lon,
            label,
            ...(description !== undefined ? { description } : {}),
          }));

        if (removedWaypoints.length > 0) {
          formData.append(
            'remove_custom_waypoints',
            JSON.stringify(removedWaypoints)
          );
        }
      }

      if (formValues.coverImage instanceof File) {
        formData.append('media', formValues.coverImage);
      } else if (
        mode === 'edit' &&
        Array.isArray(existingRoute?.media) &&
        typeof existingRoute.media[0] === 'string' &&
        existingRoute.media[0] &&
        !formValues.coverImage
      ) {
        formData.append('media', '[]');
      }

      if (
        formValues.coverImage_captions &&
        formValues.coverImage_captions.length > 0
      ) {
        formData.append(
          'image_captions',
          JSON.stringify(formValues.coverImage_captions[0])
        );
      }

      let result: ThemeRoute | { data: ThemeRoute } | undefined;

      if (mode === 'create') {
        result = await createThemeRoute(formData).unwrap();
      } else if (mode === 'edit' && formValues.id) {
        result = await updateThemeRoute({
          id: formValues.id,
          data: formData,
        }).unwrap();
      }

      let savedId = formValues.id ?? '';

      if (result) {
        if ('data' in result && result.data) {
          savedId = result.data.id;
        } else if ('id' in result) {
          savedId = result.id;
        }
      }
      enqueueSnackbar(
        mode === 'create'
          ? intl.formatMessage({
              id: 'THEME_ROUTES.SUCCESS.CREATED',
              defaultMessage: 'Theme route created successfully',
            })
          : intl.formatMessage({
              id: 'THEME_ROUTES.SUCCESS.UPDATED',
              defaultMessage: 'Theme route updated successfully',
            }),
        { variant: 'success' }
      );

      if (embedded) {
        if (onBack) {
          onBack();
        } else {
          navigate(backTarget);
        }
      } else if (mode === 'create') {
        navigate('/theme-routes');
      } else {
        navigate(`/theme-routes/view/${savedId}`);
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        intl.formatMessage({
          id: 'THEME_ROUTES.ERROR.SAVE_FAILED',
          defaultMessage: 'Failed to save theme route',
        }),
        { variant: 'error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isMapboxReady) return;
    if (mapRef.map) return;

    const container = document.getElementById('theme-routes-map-container');
    if (!container) return;

    if (!mapboxgl.supported()) {
      setWebglError(true);
      return;
    }

    try {
      const map = new mapboxgl.Map({
        container,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [24.9384, 60.1699],
        zoom: 5,
      });

      mapRef.map = map;
    } catch (e) {
      console.error('WebGL Error:', e);
      setWebglError(true);
      return;
    }

    setIsMapLoaded(false);

    mapRef.map.on('load', () => {
      if (!mapRef.map) return;

      setIsMapLoaded(true);

      const emptyCollection: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [],
      };

      if (!mapRef.map.getSource('theme-route')) {
        mapRef.map.addSource('theme-route', {
          type: 'geojson',
          data: emptyCollection,
        });

        mapRef.map.addLayer({
          id: 'theme-route-line',
          type: 'line',
          source: 'theme-route',
          paint: {
            'line-color': '#2563eb',
            'line-width': 3,
          },
        });

        mapRef.map.addLayer({
          id: 'theme-route-points',
          type: 'circle',
          source: 'theme-route',
          paint: {
            'circle-radius': 8,
            'circle-color': '#2563eb',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          },
          filter: ['==', ['get', 'pointType'], 'poi'],
        });

        mapRef.map.addLayer({
          id: 'theme-route-harbors',
          type: 'circle',
          source: 'theme-route',
          paint: {
            'circle-radius': 10,
            'circle-color': '#fbbf24',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          },
          filter: ['==', ['get', 'pointType'], 'harbor'],
        });

        mapRef.map.addLayer({
          id: 'theme-route-harbor-orders',
          type: 'symbol',
          source: 'theme-route',
          layout: {
            'text-field': ['to-string', ['get', 'order']],
            'text-size': 14,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-anchor': 'center',
            'text-offset': [0, 0],
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: {
            'text-color': '#111827',
            'text-halo-color': '#ffffff',
            'text-halo-width': 2,
          },
          filter: ['==', ['get', 'pointType'], 'harbor'],
        });

        mapRef.map.addLayer({
          id: 'theme-route-harbor-labels',
          type: 'symbol',
          source: 'theme-route',
          layout: {
            'text-field': ['coalesce', ['get', 'harborName'], 'Harbor'],
            'text-size': 11,
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-anchor': 'top',
            'text-offset': [0, 1.1],
          },
          paint: {
            'text-color': '#1f2937',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1,
          },
          filter: ['==', ['get', 'pointType'], 'harbor'],
        });

        mapRef.map.addLayer({
          id: 'theme-route-custom-waypoints',
          type: 'circle',
          source: 'theme-route',
          paint: {
            'circle-radius': 7,
            'circle-color': '#7c3aed',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          },
          filter: ['==', ['get', 'pointType'], 'custom'],
        });

        mapRef.map.addLayer({
          id: 'theme-route-custom-waypoint-labels',
          type: 'symbol',
          source: 'theme-route',
          layout: {
            'text-field': ['coalesce', ['get', 'label'], 'Waypoint'],
            'text-size': 11,
            'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
            'text-anchor': 'top',
            'text-offset': [0, 1.1],
          },
          paint: {
            'text-color': '#4c1d95',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1,
          },
          filter: ['==', ['get', 'pointType'], 'custom'],
        });

        mapRef.map.addLayer({
          id: 'theme-route-poi-labels',
          type: 'symbol',
          source: 'theme-route',
          layout: {
            'text-field': ['to-string', ['get', 'order']],
            'text-size': 14,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-anchor': 'center',
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#111827',
            'text-halo-width': 2,
          },
          filter: ['==', ['get', 'pointType'], 'poi'],
        });
      }
    });

    return () => {
      if (mapRef.map) {
        mapRef.map.remove();
        mapRef.map = null;
      }

      setIsMapLoaded(false);
    };
  }, [mapRef, isMapboxReady]);

  useEffect(() => {
    if (!mapRef.map) return;
    if (!isMapLoaded) return;

    const poiSequence = formValues.poiSequence.filter(
      (poi) =>
        typeof poi.longitude === 'number' && typeof poi.latitude === 'number'
    );

    const harborSequenceRaw = formValues.poiSequence.filter(
      (poi) =>
        typeof poi.harborLongitude === 'number' &&
        typeof poi.harborLatitude === 'number'
    );

    const harborSequence = (() => {
      const seen = new Set<string>();
      const unique: ThemeRoutePoiRef[] = [];

      harborSequenceRaw.forEach((poi) => {
        const key =
          poi.harborId ||
          poi.harborName ||
          `${poi.harborLongitude}-${poi.harborLatitude}`;
        if (seen.has(key)) return;
        seen.add(key);
        unique.push(poi);
      });

      return unique;
    })();

    const harborOrderMap = new Map<string, number>();
    harborSequence.forEach((poi, index) => {
      const order = index + 1;
      if (poi.harborId && !harborOrderMap.has(poi.harborId)) {
        harborOrderMap.set(poi.harborId, order);
      }
      if (poi.harborName && !harborOrderMap.has(poi.harborName)) {
        harborOrderMap.set(poi.harborName, order);
      }
      if (
        typeof poi.harborLongitude === 'number' &&
        typeof poi.harborLatitude === 'number'
      ) {
        const coordKey = `${poi.harborLongitude}-${poi.harborLatitude}`;
        if (!harborOrderMap.has(coordKey)) {
          harborOrderMap.set(coordKey, order);
        }
      }
    });

    const features: GeoJSON.Feature[] = [];

    if (!isCalculatingRoute) {
      const customWaypointCoords: [number, number][] = (
        Array.isArray(formValues.customWaypoints)
          ? formValues.customWaypoints
          : []
      )
        .filter(
          (waypoint) =>
            typeof waypoint.lon === 'number' && typeof waypoint.lat === 'number'
        )
        .map((waypoint) => [waypoint.lon as number, waypoint.lat as number]);

      const tourismFallbackCoords: [number, number][] =
        enablePoiHarborConnection
          ? fixedHarborCoords && poiSequence.length >= 1
            ? ([
                [fixedHarborCoords.lng, fixedHarborCoords.lat] as [
                  number,
                  number,
                ],
                ...customWaypointCoords,
                ...poiSequence.map((poi) => [
                  poi.longitude as number,
                  poi.latitude as number,
                ]),
              ] as [number, number][])
            : []
          : [];

      const routeLineCoords: [number, number][] =
        seaRouteCoords && seaRouteCoords.length >= 2
          ? seaRouteCoords
          : !enablePoiHarborConnection
            ? harborSequence.length >= 2
              ? harborSequence.map((poi) => [
                  poi.harborLongitude as number,
                  poi.harborLatitude as number,
                ])
              : []
            : tourismFallbackCoords.length >= 2
              ? tourismFallbackCoords
              : poiSequence.length >= 2
                ? poiSequence.map((poi) => [
                    poi.longitude as number,
                    poi.latitude as number,
                  ])
                : [];

      if (routeLineCoords.length >= 2) {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: routeLineCoords,
          },
        });
      }
    }

    // POI points
    poiSequence.forEach((poi, index) => {
      features.push({
        type: 'Feature',
        properties: {
          pointType: 'poi',
          name: poi.poiName,
          harborName: poi.harborName,
          description: poi.description,
          imageUrl: poi.imageUrl,
          order: index + 1,
        },
        geometry: {
          type: 'Point',
          coordinates: [poi.longitude as number, poi.latitude as number],
        },
      });
    });

    // Harbor points (unique by harborId). Use only real harbor coordinates.
    const harborPointMap = new Map<
      string,
      { name?: string; lng: number; lat: number }
    >();

    if (fixedHarborCoords) {
      harborPointMap.set(fixedHarborCoords.id, {
        name: fixedHarborCoords.name,
        lng: fixedHarborCoords.lng,
        lat: fixedHarborCoords.lat,
      });
    }

    formValues.poiSequence.forEach((poi) => {
      if (
        typeof poi.harborLongitude !== 'number' ||
        typeof poi.harborLatitude !== 'number'
      ) {
        return;
      }

      const key =
        poi.harborId ||
        poi.harborName ||
        `${poi.harborLongitude}-${poi.harborLatitude}`;
      if (harborPointMap.has(key)) return;

      harborPointMap.set(key, {
        name: poi.harborName,
        lng: poi.harborLongitude,
        lat: poi.harborLatitude,
      });
    });

    harborPointMap.forEach((value, mapKey) => {
      const coordKey = `${value.lng}-${value.lat}`;
      const orderByMapKey = harborOrderMap.get(mapKey);
      const orderByCoord = harborOrderMap.get(coordKey);
      const orderByName = value.name
        ? harborOrderMap.get(value.name)
        : undefined;
      const order =
        fixedHarborCoords &&
        value.lng === fixedHarborCoords.lng &&
        value.lat === fixedHarborCoords.lat
          ? 1
          : orderByMapKey || orderByCoord || orderByName;

      features.push({
        type: 'Feature',
        properties: {
          pointType: 'harbor',
          harborName: value.name,
          order,
        },
        geometry: {
          type: 'Point',
          coordinates: [value.lng, value.lat],
        },
      });
    });

    const customWaypoints = Array.isArray(formValues.customWaypoints)
      ? formValues.customWaypoints
      : [];

    customWaypoints.forEach((waypoint) => {
      if (
        typeof waypoint.lon !== 'number' ||
        typeof waypoint.lat !== 'number'
      ) {
        return;
      }

      features.push({
        type: 'Feature',
        properties: {
          pointType: 'custom',
          label: waypoint.label,
          description: waypoint.description,
        },
        geometry: {
          type: 'Point',
          coordinates: [waypoint.lon, waypoint.lat],
        },
      });
    });

    const collection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    const source = mapRef.map.getSource(
      'theme-route'
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(collection);
    }

    const bounds = new mapboxgl.LngLatBounds();

    if (harborPointMap.size > 0) {
      harborPointMap.forEach((value) => {
        bounds.extend([value.lng, value.lat]);
      });
    } else if (poiSequence.length > 0) {
      poiSequence.forEach((poi) => {
        bounds.extend([poi.longitude as number, poi.latitude as number]);
      });
    } else if (customWaypoints.length > 0) {
      customWaypoints.forEach((waypoint) => {
        if (
          typeof waypoint.lon !== 'number' ||
          typeof waypoint.lat !== 'number'
        ) {
          return;
        }
        bounds.extend([waypoint.lon, waypoint.lat]);
      });
    }

    if (!bounds.isEmpty()) {
      mapRef.map.fitBounds(bounds, { padding: 40, maxZoom: 10, duration: 500 });
    }
  }, [
    formValues.poiSequence,
    formValues.customWaypoints,
    seaRouteCoords,
    fixedHarborCoords,
    mapRef,
    enablePoiHarborConnection,
    isCalculatingRoute,
    isMapLoaded,
  ]);

  const title =
    mode === 'create'
      ? intl.formatMessage({
          id: 'THEME_ROUTES.FORM.CREATE.TITLE',
          defaultMessage: 'Create Theme Route',
        })
      : mode === 'edit'
        ? intl.formatMessage({
            id: 'THEME_ROUTES.FORM.EDIT.TITLE',
            defaultMessage: 'Edit Theme Route',
          })
        : intl.formatMessage({
            id: 'THEME_ROUTES.FORM.VIEW.TITLE',
            defaultMessage: 'View Theme Route',
          });

  const description =
    mode === 'create'
      ? intl.formatMessage({
          id: 'THEME_ROUTES.FORM.CREATE.DESCRIPTION',
          defaultMessage:
            'Define metadata and points of interest for a new themed maritime route.',
        })
      : mode === 'edit'
        ? intl.formatMessage({
            id: 'THEME_ROUTES.FORM.EDIT.DESCRIPTION',
            defaultMessage:
              'Update metadata or the POI sequence for this theme route.',
          })
        : intl.formatMessage({
            id: 'THEME_ROUTES.FORM.VIEW.DESCRIPTION',
            defaultMessage:
              'View the configuration and sequence of this theme route.',
          });

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle text={title} />
            <ToolbarDescription>{description}</ToolbarDescription>
          </ToolbarHeading>
          <div className="flex items-center gap-2">
            <div className="mt-2 md:mt-0 relative">
              <label htmlFor="language-select" className="sr-only">
                {intl.formatMessage({ id: 'USER.MENU.LANGUAGE' })}
              </label>
              <select
                id="language-select"
                className="select select-bordered pl-8 pr-8 h-10 md:h-9 leading-normal"
                value={currentLanguage.code}
                onChange={(e) => {
                  const selectedLang = I18N_LANGUAGES.find(
                    (lang) => lang.code === e.target.value
                  );
                  if (selectedLang) {
                    changeLanguage(selectedLang);
                  }
                }}
                aria-label={intl.formatMessage({ id: 'USER.MENU.LANGUAGE' })}
              >
                {I18N_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <img
                src="/media/flags/globe.svg"
                alt="Globe"
                className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none dark:brightness-0 dark:invert contrast:brightness-0 contrast:invert"
                aria-hidden="true"
              />
            </div>
            {!isViewMode &&
              formValues.description &&
              formValues.description.trim() && (
                <button
                  type="button"
                  onClick={handleOpenPreview}
                  className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label={intl.formatMessage({
                    id: 'THEME_ROUTES.FORM.SHOW_PREVIEW.ARIA',
                    defaultMessage: 'Show description preview',
                  })}
                  disabled={isSubmitting}
                >
                  <KeenIcon
                    type="filled"
                    icon="eye"
                    className="text-gray-500"
                  />
                  {intl.formatMessage({
                    id: 'COMMON.SHOW_PREVIEW',
                    defaultMessage: 'Show Preview',
                  })}
                </button>
              )}
            <button
              type="button"
              onClick={() => {
                if (embedded && onBack) {
                  onBack();
                } else {
                  navigate(backTarget);
                }
              }}
              className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={
                mode === 'view'
                  ? intl.formatMessage({
                      id: 'THEME_ROUTES.FORM.BACK.ARIA',
                      defaultMessage: 'Back to theme routes',
                    })
                  : intl.formatMessage({
                      id: 'THEME_ROUTES.FORM.CANCEL.ARIA',
                      defaultMessage: 'Cancel and go back to theme routes',
                    })
              }
            >
              <KeenIcon type="filled" icon="cross" className="text-gray-500" />
              {mode === 'view'
                ? intl.formatMessage({
                    id: 'COMMON.BACK',
                    defaultMessage: 'Back',
                  })
                : intl.formatMessage({
                    id: 'COMMON.CANCEL',
                    defaultMessage: 'Cancel',
                  })}
            </button>
          </div>
        </Toolbar>
      </Container>

      <Container>
        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 gap-6 lg:gap-8">
            <div className="space-y-6">
              <div className="card">
                <div className="card-header">
                  <div className="card-title text-base font-semibold text-gray-800">
                    {intl.formatMessage({
                      id: 'THEME_ROUTES.FORM.ROUTE_METADATA.TITLE',
                      defaultMessage: 'Route metadata',
                    })}
                  </div>
                </div>
                <div className="card-body space-y-4">
                  <div>
                    <label
                      htmlFor="route-name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({
                        id: 'COMMON.NAME',
                        defaultMessage: 'Name',
                      })}{' '}
                      ({formValues.defaultLanguage.toUpperCase()})
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="route-name"
                      type="text"
                      value={formValues.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      autoFocus={!isViewMode}
                      disabled={isViewMode || isSubmitting}
                      className={clsx(
                        'input input-bordered w-full h-11 rounded-lg border-gray-300',
                        errors.name && 'border-red-500 focus:ring-red-500'
                      )}
                      placeholder={intl.formatMessage({
                        id: 'THEME_ROUTES.FORM.NAME.PLACEHOLDER',
                        defaultMessage: 'Enter route name in selected language',
                      })}
                      aria-invalid={!!errors.name}
                      aria-describedby={
                        errors.name ? 'route-name-error' : undefined
                      }
                      autoComplete="off"
                    />
                    {errors.name && (
                      <p
                        id="route-name-error"
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="route-description"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({
                        id: 'COMMON.DESCRIPTION',
                        defaultMessage: 'Description',
                      })}{' '}
                      ({currentLanguage.code.toUpperCase()})
                    </label>
                    <textarea
                      id="route-description"
                      rows={4}
                      value={
                        activeLanguage === formValues.defaultLanguage
                          ? formValues.description || ''
                          : activeTranslation?.description || ''
                      }
                      onChange={(e) => handleDescriptionChange(e.target.value)}
                      disabled={isViewMode || isSubmitting}
                      className="textarea textarea-bordered w-full rounded-lg border-gray-300"
                      placeholder={intl.formatMessage({
                        id: 'THEME_ROUTES.FORM.DESCRIPTION.PLACEHOLDER',
                        defaultMessage:
                          'Describe the experience and highlights of this route',
                      })}
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <MediaUpload<ThemeRouteFormValues>
                      field="coverImage"
                      media={formValues.coverImage}
                      setFormData={
                        setFormValues as Dispatch<
                          SetStateAction<Partial<ThemeRouteFormValues>>
                        >
                      }
                      errors={errors}
                      setErrors={setErrors}
                      disabled={isViewMode || isSubmitting}
                      allowedTypes={[
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                        'image/gif',
                        'image/webp',
                      ]}
                      maxFiles={1}
                      previewMode={true}
                      media_captions={formValues.coverImage_captions}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="route-theme"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {intl.formatMessage({
                          id: 'THEME_ROUTES.FORM.THEME.LABEL',
                          defaultMessage: 'Theme',
                        })}
                      </label>
                      <select
                        id="route-theme"
                        value={formValues.themeKey}
                        onChange={handleThemeChange}
                        disabled={isViewMode || isSubmitting}
                        className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                      >
                        {THEME_ROUTE_THEME_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {intl.formatMessage({
                              id: `THEME_ROUTES.FORM.THEME.OPTION.${opt.value.toUpperCase()}`,
                              defaultMessage: opt.label,
                            })}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="card-title text-base font-semibold text-gray-800">
                    {intl.formatMessage({
                      id: 'THEME_ROUTES.FORM.POI_BUILDER.TITLE',
                      defaultMessage: 'POI builder',
                    })}
                  </div>
                  {seaRouteDistanceNm != null && seaRouteDistanceNm > 0 && (
                    <div className="text-xs text-gray-600 mt-1">
                      {intl.formatMessage(
                        {
                          id: 'THEME_ROUTES.FORM.POI_BUILDER.TOTAL_DISTANCE',
                          defaultMessage:
                            'Total sea route distance: {distance} NM',
                        },
                        {
                          distance: formatFinnishNumber(seaRouteDistanceNm),
                        }
                      )}
                    </div>
                  )}
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Column 1: Harbor search & selection */}
                    <section
                      aria-label={intl.formatMessage({
                        id: 'THEME_ROUTES.FORM.POI_BUILDER.SECTION_HARBOR.ARIA',
                        defaultMessage: 'Select harbor to filter POIs',
                      })}
                    >
                      <h2 className="text-sm font-semibold text-gray-800 mb-2">
                        {intl.formatMessage({
                          id: 'THEME_ROUTES.FORM.POI_BUILDER.STEP1.TITLE',
                          defaultMessage: '1. Choose harbor',
                        })}
                      </h2>
                      <p className="text-xs text-gray-500 mb-3">
                        {intl.formatMessage({
                          id: 'THEME_ROUTES.FORM.POI_BUILDER.STEP1.DESCRIPTION',
                          defaultMessage:
                            'Use the search box to find a harbor. Selecting a harbor will filter the POIs in the middle column.',
                        })}
                      </p>
                      {fixedHarborId && (
                        <div
                          className="rounded-lg border border-gray-200 bg-gray-100 px-3 py-2"
                          aria-label={intl.formatMessage({
                            id: 'THEME_ROUTES.FORM.POI_BUILDER.FIXED_HARBOR.ARIA',
                            defaultMessage: 'Selected harbor (locked)',
                          })}
                        >
                          <div className="text-xs font-medium text-gray-800">
                            {intl.formatMessage({
                              id: 'THEME_ROUTES.FORM.POI_BUILDER.FIXED_HARBOR.LABEL',
                              defaultMessage: 'Selected harbor',
                            })}
                          </div>
                          <div className="text-sm font-semibold text-gray-900">
                            {fixedHarborCoords?.name ||
                              intl.formatMessage({
                                id: 'THEME_ROUTES.FORM.POI_BUILDER.FIXED_HARBOR.FALLBACK',
                                defaultMessage: 'Harbor selected',
                              })}
                          </div>
                          {fixedHarborCoords && (
                            <div className="mt-1 text-xs text-gray-800">
                              {' '}
                              {formatFinnishCoordinate(fixedHarborCoords.lat, {
                                minimumFractionDigits: 5,
                                maximumFractionDigits: 5,
                              })}{' '}
                              ,{' '}
                              {formatFinnishCoordinate(fixedHarborCoords.lng, {
                                minimumFractionDigits: 5,
                                maximumFractionDigits: 5,
                              })}
                            </div>
                          )}
                        </div>
                      )}
                      {!fixedHarborId && (
                        <>
                          <div className="mb-2">
                            <label htmlFor="harbor-search" className="sr-only">
                              {intl.formatMessage({
                                id: 'THEME_ROUTES.FORM.POI_BUILDER.SEARCH_HARBORS.LABEL',
                                defaultMessage: 'Search harbors',
                              })}
                            </label>
                            <input
                              id="harbor-search"
                              type="text"
                              value={harborSearchTerm}
                              onChange={(e) =>
                                setHarborSearchTerm(e.target.value)
                              }
                              className="input input-sm h-8 w-full text-sm border-gray-300"
                              placeholder={intl.formatMessage({
                                id: 'THEME_ROUTES.FORM.POI_BUILDER.SEARCH_HARBORS.PLACEHOLDER',
                                defaultMessage: 'Search harbors',
                              })}
                              aria-label={intl.formatMessage({
                                id: 'THEME_ROUTES.FORM.POI_BUILDER.SEARCH_HARBORS.ARIA',
                                defaultMessage: 'Search harbors by name',
                              })}
                            />
                          </div>
                          <label
                            htmlFor="harbor-filter"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            {intl.formatMessage({
                              id: 'THEME_ROUTES.FORM.POI_BUILDER.HARBOR_FILTER.LABEL',
                              defaultMessage: 'Harbor',
                            })}
                          </label>
                          <select
                            id="harbor-filter"
                            value={selectedHarborId}
                            onChange={(e) =>
                              setSelectedHarborId(e.target.value)
                            }
                            disabled={isSubmitting}
                            className="select select-bordered w-full h-10 rounded-lg border-gray-300"
                          >
                            <option value="" disabled>
                              {intl.formatMessage({
                                id: 'THEME_ROUTES.FORM.POI_BUILDER.HARBOR_FILTER.PLACEHOLDER',
                                defaultMessage: 'Select harbor',
                              })}
                            </option>
                            {harborOptions.map((harbor) => (
                              <option key={harbor.value} value={harbor.value}>
                                {harbor.label}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                      {!fixedHarborId && !isViewMode && (
                        <button
                          type="button"
                          onClick={handleAddHarborStop}
                          disabled={!selectedHarborId || isSubmitting}
                          className="btn btn-xs btn-light mt-2 w-full cursor-pointer"
                          aria-label={intl.formatMessage({
                            id: 'THEME_ROUTES.FORM.POI_BUILDER.ADD_HARBOR_STOP.ARIA',
                            defaultMessage:
                              'Add selected harbor as a route stop without POI',
                          })}
                        >
                          {intl.formatMessage({
                            id: 'THEME_ROUTES.FORM.POI_BUILDER.ADD_HARBOR_STOP',
                            defaultMessage: 'Add harbor stop (no POI)',
                          })}
                        </button>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {intl.formatMessage({
                          id: 'THEME_ROUTES.FORM.POI_BUILDER.CUSTOM_WAYPOINT_HINT',
                          defaultMessage:
                            'After selecting two harbors, you can add custom waypoints on the map to guide the route (our routing engine follows waterbodies).',
                        })}
                      </p>
                    </section>

                    {/* Column 2: Available POIs */}
                    <section aria-label="Available POIs to add to route">
                      <h2 className="text-sm font-semibold text-gray-800 mb-2">
                        {intl.formatMessage({
                          id: 'THEME_ROUTES.FORM.POI_BUILDER.STEP2.TITLE',
                          defaultMessage: '2. Add POIs',
                        })}
                      </h2>
                      <p className="text-xs text-gray-500 mb-3">
                        {intl.formatMessage({
                          id: 'THEME_ROUTES.FORM.POI_BUILDER.STEP2.DESCRIPTION',
                          defaultMessage:
                            'Click a POI to add it to the route sequence on the right.',
                        })}
                      </p>
                      <div
                        className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto p-2 custom-scrollbar"
                        role="list"
                        aria-label={intl.formatMessage({
                          id: 'THEME_ROUTES.FORM.POI_BUILDER.AVAILABLE_POIS.ARIA',
                          defaultMessage: 'Available points of interest',
                        })}
                      >
                        {!selectedHarborId ? (
                          <p
                            className="text-sm text-gray-500 px-2 py-1"
                            role="listitem"
                          >
                            {intl.formatMessage({
                              id: 'THEME_ROUTES.FORM.POI_BUILDER.NO_HARBOR_MESSAGE',
                              defaultMessage:
                                'Select a harbor in step 1 to see its POIs here.',
                            })}
                          </p>
                        ) : isPoisLoading ? (
                          <p
                            className="text-sm text-gray-500 px-2 py-1"
                            role="listitem"
                          >
                            {intl.formatMessage({
                              id: 'THEME_ROUTES.FORM.POI_BUILDER.LOADING_POIS',
                              defaultMessage:
                                'Loading POIs for the selected harbor...',
                            })}
                          </p>
                        ) : poiOptions.length === 0 ? (
                          <p
                            className="text-sm text-gray-500 px-2 py-1"
                            role="listitem"
                          >
                            {intl.formatMessage({
                              id: 'THEME_ROUTES.FORM.POI_BUILDER.NO_POIS',
                              defaultMessage:
                                'No POIs found for the selected harbor.',
                            })}
                          </p>
                        ) : (
                          poiOptions.map((poi) => (
                            <button
                              key={poi.id}
                              type="button"
                              onClick={() => handleAddPoi(poi)}
                              role="listitem"
                              className="w-full text-left text-sm px-2 py-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 flex flex-col"
                              aria-label={intl.formatMessage(
                                {
                                  id: 'THEME_ROUTES.FORM.POI_BUILDER.ADD_POI.ARIA',
                                  defaultMessage: 'Add POI {name} to route',
                                },
                                { name: poi.name }
                              )}
                            >
                              <span className="font-medium text-gray-900">
                                {poi.name}
                              </span>
                              {poi.harbors && poi.harbors.length > 0 && (
                                <span className="text-xs text-gray-500">
                                  {poi.harbors[0].name}
                                </span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </section>

                    {/* Column 3: Route sequence */}
                    <section
                      aria-label={intl.formatMessage({
                        id: 'THEME_ROUTES.FORM.POI_BUILDER.SECTION_ROUTE.ARIA',
                        defaultMessage: 'Ordered route sequence',
                      })}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-sm font-semibold text-gray-800">
                          {intl.formatMessage({
                            id: 'THEME_ROUTES.FORM.POI_BUILDER.STEP3.TITLE',
                            defaultMessage: '3. Arrange route',
                          })}
                        </h2>
                        <span className="text-xs text-gray-500">
                          {formValues.poiSequence.length} POIs
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        {intl.formatMessage({
                          id: 'THEME_ROUTES.FORM.POI_BUILDER.STEP3.DESCRIPTION',
                          defaultMessage:
                            'Use the arrows or keyboard (↑ ↓ Delete) to reorder or remove POIs.',
                        })}
                      </p>
                      <div
                        className={clsx(
                          'border rounded-lg max-h-64 overflow-y-auto p-2 custom-scrollbar',
                          errors.poiSequence && 'border-red-500'
                        )}
                        role="list"
                        aria-label={intl.formatMessage({
                          id: 'THEME_ROUTES.FORM.POI_BUILDER.ROUTE_LIST.ARIA',
                          defaultMessage: 'Ordered list of POIs in this route',
                        })}
                      >
                        {formValues.poiSequence.length === 0 ? (
                          <p
                            className="text-sm text-gray-500 px-2 py-1"
                            role="listitem"
                          >
                            {intl.formatMessage({
                              id: 'THEME_ROUTES.FORM.POI_BUILDER.ROUTE_LIST.EMPTY',
                              defaultMessage:
                                'No POIs added yet. Select POIs from the middle column to build the route.',
                            })}
                          </p>
                        ) : (
                          formValues.poiSequence.map((poi, index) => (
                            <div
                              key={`${poi.poiId}-${index}`}
                              role="listitem"
                              tabIndex={0}
                              onKeyDown={(e) => handlePoiKeyDown(e, index)}
                              className="flex items-center justify-between px-2 py-1 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-1 contrast:hover:bg-black/60"
                              aria-label={intl.formatMessage(
                                {
                                  id: 'THEME_ROUTES.FORM.POI_BUILDER.ROUTE_LIST.ITEM_ARIA',
                                  defaultMessage:
                                    'POI {index}: {poiName}{harborPart}',
                                },
                                {
                                  index: index + 1,
                                  poiName:
                                    poi.poiName ||
                                    intl.formatMessage({
                                      id: 'THEME_ROUTES.FORM.POI_BUILDER.ROUTE_LIST.UNNAMED',
                                      defaultMessage: 'Unnamed',
                                    }),
                                  harborPart: poi.harborName
                                    ? intl.formatMessage(
                                        {
                                          id: 'THEME_ROUTES.FORM.POI_BUILDER.ROUTE_LIST.HARBOR_PART',
                                          defaultMessage:
                                            ', harbor {harborName}',
                                        },
                                        { harborName: poi.harborName }
                                      )
                                    : '',
                                }
                              )}
                            >
                              <div className="flex flex-col text-sm text-gray-800">
                                <span className="font-medium flex items-center gap-2">
                                  <span
                                    className="inline-flex items-center justify-center rounded-full bg-gray-200 text-gray-900 text-xs font-semibold w-6 h-6"
                                    aria-label={intl.formatMessage(
                                      {
                                        id: 'THEME_ROUTES.FORM.POI_BUILDER.ROUTE_LIST.ORDER_BADGE.ARIA',
                                        defaultMessage: 'Order {index}',
                                      },
                                      { index: index + 1 }
                                    )}
                                  >
                                    {index + 1}
                                  </span>
                                  {poi.poiName ||
                                    intl.formatMessage({
                                      id: 'THEME_ROUTES.FORM.POI_BUILDER.ROUTE_LIST.UNNAMED',
                                      defaultMessage: 'Unnamed',
                                    })}
                                </span>
                                {poi.harborName && (
                                  <span className="text-xs text-gray-500">
                                    {poi.harborName}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 cursor-pointer">
                                <button
                                  type="button"
                                  onClick={() => movePoi(index, index - 1)}
                                  disabled={index === 0 || isSubmitting}
                                  aria-label={intl.formatMessage({
                                    id: 'THEME_ROUTES.FORM.POI_BUILDER.ROUTE_LIST.MOVE_UP.ARIA',
                                    defaultMessage: 'Move POI up',
                                  })}
                                  className="btn btn-xs btn-light"
                                >
                                  <KeenIcon
                                    type="filled"
                                    icon="arrow-up"
                                    className="text-gray-600"
                                  />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => movePoi(index, index + 1)}
                                  disabled={
                                    index ===
                                      formValues.poiSequence.length - 1 ||
                                    isSubmitting
                                  }
                                  aria-label={intl.formatMessage({
                                    id: 'THEME_ROUTES.FORM.POI_BUILDER.ROUTE_LIST.MOVE_DOWN.ARIA',
                                    defaultMessage: 'Move POI down',
                                  })}
                                  className="btn btn-xs btn-light"
                                >
                                  <KeenIcon
                                    type="filled"
                                    icon="arrow-down"
                                    className="text-gray-600"
                                  />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemovePoi(index)}
                                  disabled={isSubmitting}
                                  aria-label={intl.formatMessage({
                                    id: 'THEME_ROUTES.FORM.POI_BUILDER.ROUTE_LIST.REMOVE.ARIA',
                                    defaultMessage: 'Remove POI from route',
                                  })}
                                  className="btn btn-xs btn-light"
                                >
                                  <KeenIcon
                                    type="filled"
                                    icon="trash"
                                    className="text-red-500"
                                  />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      {errors.poiSequence && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.poiSequence}
                        </p>
                      )}
                    </section>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="card-title text-base font-semibold text-gray-800">
                    {intl.formatMessage({
                      id: 'THEME_ROUTES.FORM.CUSTOM_WAYPOINTS.TITLE',
                      defaultMessage: 'Custom waypoints',
                    })}
                  </div>
                  {!isViewMode && (
                    <button
                      type="button"
                      className={clsx(
                        'btn btn-sm',
                        isAddingCustomWaypoint ? 'btn-primary' : 'btn-light'
                      )}
                      onClick={() => setIsAddingCustomWaypoint((prev) => !prev)}
                      disabled={isSubmitting}
                    >
                      {isAddingCustomWaypoint
                        ? intl.formatMessage({
                            id: 'THEME_ROUTES.FORM.CUSTOM_WAYPOINTS.ADDING',
                            defaultMessage: 'Click on map…',
                          })
                        : intl.formatMessage({
                            id: 'THEME_ROUTES.FORM.CUSTOM_WAYPOINTS.ADD',
                            defaultMessage: 'Add from map',
                          })}
                    </button>
                  )}
                </div>
                <div className="card-body space-y-3">
                  <p className="text-sm text-gray-600">
                    {intl.formatMessage({
                      id: 'THEME_ROUTES.FORM.CUSTOM_WAYPOINTS.DESCRIPTION',
                      defaultMessage:
                        'Add landmarks, safe turns, bridge passages, or narrative points that are not POIs. Use ‘Add from map’ then click the map to drop a waypoint.',
                    })}
                  </p>

                  {errors.customWaypoints && (
                    <p className="text-red-500 text-xs">
                      {errors.customWaypoints}
                    </p>
                  )}

                  {Array.isArray(formValues.customWaypoints) &&
                  formValues.customWaypoints.length > 0 ? (
                    <div className="space-y-2">
                      {formValues.customWaypoints.map((waypoint, index) => {
                        const isExpanded =
                          expandedCustomWaypointIndex === index;
                        const descriptionValue =
                          getWaypointDescriptionForLanguage(
                            waypoint,
                            activeLanguage
                          ).trim();
                        const coordsLabel =
                          typeof waypoint.lat === 'number' &&
                          typeof waypoint.lon === 'number'
                            ? `${formatFinnishCoordinate(waypoint.lat, {
                                minimumFractionDigits: 6,
                                maximumFractionDigits: 6,
                              })}, ${formatFinnishCoordinate(waypoint.lon, {
                                minimumFractionDigits: 6,
                                maximumFractionDigits: 6,
                              })}`
                            : '';

                        return (
                          <div
                            key={`${waypoint.lon}-${waypoint.lat}-${index}`}
                            className="border border-gray-200 rounded-lg px-3 py-2"
                          >
                            <div className="flex flex-col gap-2">
                              <div className="flex flex-col md:flex-row md:items-center gap-2">
                                <div className="text-xs text-gray-500 w-10 shrink-0">
                                  #{index + 1}
                                </div>

                                <div className="flex-1">
                                  <input
                                    id={`custom-waypoint-label-${index}`}
                                    type="text"
                                    value={waypoint.label || ''}
                                    onChange={(e) =>
                                      handleUpdateCustomWaypoint(index, {
                                        label: e.target.value,
                                      })
                                    }
                                    disabled={isViewMode || isSubmitting}
                                    className="input input-bordered w-full h-9 rounded-md border-gray-300"
                                    placeholder={intl.formatMessage({
                                      id: 'THEME_ROUTES.FORM.CUSTOM_WAYPOINTS.LABEL.PLACEHOLDER',
                                      defaultMessage: 'Label *',
                                    })}
                                    autoComplete="off"
                                  />
                                </div>

                                <div className="text-xs text-gray-600 md:w-56 shrink-0">
                                  {coordsLabel}
                                </div>

                                <div className="flex items-center justify-end gap-2 shrink-0">
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-light"
                                    onClick={() =>
                                      setExpandedCustomWaypointIndex((prev) =>
                                        prev === index ? null : index
                                      )
                                    }
                                    disabled={isSubmitting}
                                  >
                                    {isExpanded
                                      ? intl.formatMessage({
                                          id: 'COMMON.CLOSE',
                                          defaultMessage: 'Close',
                                        })
                                      : intl.formatMessage({
                                          id: 'COMMON.EDIT',
                                          defaultMessage: 'Edit',
                                        })}
                                  </button>

                                  {!isViewMode && (
                                    <button
                                      type="button"
                                      className="btn btn-xs btn-light"
                                      onClick={() =>
                                        handleRemoveCustomWaypoint(index)
                                      }
                                      disabled={isSubmitting}
                                    >
                                      {intl.formatMessage({
                                        id: 'COMMON.REMOVE',
                                        defaultMessage: 'Remove',
                                      })}
                                    </button>
                                  )}
                                </div>
                              </div>

                              {!isExpanded && descriptionValue && (
                                <div className="text-xs text-gray-500 pl-12 pr-2 truncate">
                                  {descriptionValue}
                                </div>
                              )}

                              {isExpanded && (
                                <div className="grid grid-cols-1 gap-2">
                                  <textarea
                                    id={`custom-waypoint-description-${index}`}
                                    rows={2}
                                    value={getWaypointDescriptionForLanguage(
                                      waypoint,
                                      activeLanguage
                                    )}
                                    onChange={(e) =>
                                      handleUpdateCustomWaypoint(index, {
                                        description: e.target.value,
                                      })
                                    }
                                    disabled={isViewMode || isSubmitting}
                                    className="textarea textarea-bordered w-full rounded-md border-gray-300"
                                    placeholder={intl.formatMessage({
                                      id: 'THEME_ROUTES.FORM.CUSTOM_WAYPOINTS.DESCRIPTION.PLACEHOLDER',
                                      defaultMessage: 'Optional description',
                                    })}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {intl.formatMessage({
                        id: 'THEME_ROUTES.FORM.CUSTOM_WAYPOINTS.EMPTY',
                        defaultMessage: 'No custom waypoints added yet.',
                      })}
                    </p>
                  )}
                </div>
              </div>

              {!isViewMode && (
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      if (embedded && onBack) {
                        onBack();
                      } else {
                        navigate(backTarget);
                      }
                    }}
                    className="btn btn-light border border-gray-300 rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isSubmitting}
                  >
                    {intl.formatMessage({
                      id: 'COMMON.CANCEL',
                      defaultMessage: 'Cancel',
                    })}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isSubmitting || !canSubmit}
                  >
                    {mode === 'create'
                      ? intl.formatMessage({
                          id: 'COMMON.CREATE',
                          defaultMessage: 'Create',
                        })
                      : intl.formatMessage({
                          id: 'COMMON.SAVE_CHANGES',
                          defaultMessage: 'Save changes',
                        })}
                  </button>
                </div>
              )}
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title text-base font-semibold text-gray-800">
                  {intl.formatMessage({
                    id: 'THEME_ROUTES.FORM.MAP_PREVIEW.TITLE',
                    defaultMessage: 'Map preview',
                  })}
                </div>
              </div>
              <div className="card-body space-y-3">
                <p className="text-sm text-gray-600">
                  {intl.formatMessage({
                    id: 'THEME_ROUTES.FORM.MAP_PREVIEW.DESCRIPTION',
                    defaultMessage:
                      'The map shows the POIs and route line in the current order. Use the POI builder on the left to add, remove, or reorder points and the map will update automatically.',
                  })}
                </p>
                {!isViewMode && isAddingCustomWaypoint && (
                  <p className="text-sm text-violet-700">
                    {intl.formatMessage({
                      id: 'THEME_ROUTES.FORM.MAP_PREVIEW.CUSTOM_WAYPOINTS_HINT',
                      defaultMessage:
                        'Custom waypoint mode is active: click on the map to add a waypoint.',
                    })}
                  </p>
                )}
                {webglError ? (
                  <div className="w-full min-h-[400px] flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 p-4 sm:p-8 shadow-sm">
                    <div className="max-w-md text-center">
                      <div className="flex justify-center mb-4">
                        <KeenIcon
                          icon="shield-cross"
                          className="text-4xl text-danger"
                        />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {intl.formatMessage({ id: 'COMMON.WEBGL_ERROR_TITLE' })}
                      </h3>
                      <p className="text-gray-600 mb-6 font-medium">
                        {intl.formatMessage({
                          id: 'COMMON.WEBGL_ERROR_MESSAGE',
                        })}
                      </p>
                      <div className="bg-gray-200 p-5 rounded-xl border border-gray-200 text-left shadow-sm">
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-3">
                          {intl.formatMessage({
                            id: 'COMMON.WEBGL_HOW_TO_ENABLE',
                          })}
                        </p>
                        <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                          {intl.formatMessage({
                            id: 'COMMON.WEBGL_ERROR_INSTRUCTIONS',
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    id="theme-routes-map-container"
                    className="w-full h-[400px] rounded-lg overflow-hidden border border-gray-200 shadow-sm"
                    role="region"
                    aria-label={intl.formatMessage({
                      id: 'THEME_ROUTES.FORM.MAP_PREVIEW.ARIA',
                      defaultMessage: 'Theme route map preview',
                    })}
                  />
                )}
              </div>
            </div>

            {isViewMode && !hideComments && (
              <div className="card">
                <div className="card-header">
                  <div className="card-title text-base font-semibold text-gray-800">
                    Reviewer comments
                  </div>
                </div>
                <div className="card-body space-y-3">
                  {isCommentsLoading ? (
                    <p className="text-sm text-gray-600">Loading comments...</p>
                  ) : comments.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No comments have been added for this theme route yet.
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {comments.map((comment) => {
                        const author =
                          comment.user?.name ||
                          comment.user?.email ||
                          comment.user_id ||
                          'Unknown user';
                        const createdAtLabel = comment.created_at
                          ? formatDisplayDateTime(new Date(comment.created_at))
                          : '';

                        return (
                          <li
                            key={comment.id}
                            className="border border-gray-200 rounded-lg p-3 bg-gray-50 contrast:bg-blue-700"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-900 contrast:text-white">
                                {author}
                              </span>
                              {createdAtLabel && (
                                <span className="text-xs text-gray-500 contrast:text-white">
                                  {createdAtLabel}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-800 whitespace-pre-line contrast:text-white">
                              {comment.comment}
                            </p>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </form>
      </Container>

      <ThemeRoutePreviewModal
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        name={formValues.name}
        defaultLanguage={formValues.defaultLanguage}
        description={formValues.description}
        translations={formValues.translations}
        source_language={formValues.defaultLanguage}
        onTranslationsGenerated={handleTranslationsGenerated}
      />
    </>
  );
};

export default ThemeRouteFormPage;
